import React, {
    Suspense,
    useLayoutEffect,
    useState,
    useEffect,
    useMemo,
} from "react";
import {
    BrowserRouter,
    Routes,
    Route,
    Navigate,
    Outlet,
    useLocation,
} from "react-router-dom";
import jwt from "jsonwebtoken";
import { useSelector } from "react-redux";
import { store } from "./config/store/app.store";

import { I18nextProvider, initReactI18next } from "react-i18next";
import i18next from "i18next";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FlagsProvider } from "flagged";
import featureMap from "./config/features/features.json";
import abilityMap from "./config/features/ability.json";

import common_de from "./config/translations/de/common.json";
import common_en from "./config/translations/en/common.json";
import common_hi from "./config/translations/hi/common.json";
import common_mr from "./config/translations/mr/common.json";
import common_bn from "./config/translations/bn/common.json";
import common_kn from "./config/translations/kn/common.json";
import common_ml from "./config/translations/ml/common.json";
import common_ta from "./config/translations/ta/common.json";
import common_te from "./config/translations/te/common.json";

import "semantic-ui-css/semantic.min.css";
import "./index.css";
import "@styles/overrule.scss";
import { devMode } from "./utilities/helpers";
import { settings } from "./config/settings/app.settings";
import { apiV6, targetRoute } from "./config/api/apiV6";
import { logout } from "@reducers/authSlice";

//-----------------------------------------------------------------------------
// Player Modules
//-----------------------------------------------------------------------------
import Register from "./pages/authentication/Registration.react";
import Login from "./pages/authentication/Login.react";
import Logout from "./pages/authentication/Logout.react";
import ForgotPassword from "./pages/authentication/ForgotPassword.react";
import ChangePassword from "./pages/authentication/ChangePassword.react";
import Home from "./pages/player/home/Home.react";
import Library from "./pages/player/courses/Library.react";
import Course from "./pages/player/courses/Course.react";
import SingleCourse from "./pages/player/courses/SingleCourse.react";
import Program from "./pages/player/courses/Program.react";
import Article from "./pages/player/articles/Article.react";
import ArticleListing from "./pages/player/articles/ArticleListing.react";
import Social from "./pages/player/social/Social.react";
import AppReports from "./pages/player/reports/AppReports.react";
import AppLeaderboard from "./pages/player/reports/AppLeaderboard.react";
import AppCollectibles from "./pages/player/reports/AppCollectibles.react";
import Contest from "./pages/player/contests/Contest.react";
import EditProfile from "./pages/player/user/EditProfile.react";
import AppHome from "./pages/player/home/AppHome.react";
import SearchResults from "./pages/player/home/panels/SearchResults.react";
import GamifixPerformance from "./pages/player/client/ekart/GamifixPerformance.react";
import CleanAppLeaderboard from "./pages/player/reports/CleanAppLeaderboard.react";
import CleanAppCollectibles from "./pages/player/reports/CleanAppCollectibles.react";
import ComingSoon from "./pages/player/home/ComingSoon.react";

import HotSpotEditor from "./pages/creator/courses/HotSpotEditor.react";
import ArticleEditor from "./pages/creator/articles/ArticleEditor.react";
import BuilderHome from "./pages/creator/home/BuilderHome.react";
import BuilderLibrary from "./pages/creator/library/BuilderLibrary.react";
import DeckEditor from "./pages/creator/decks/DeckEditor.react";
import CourseEditor from "./pages/creator/courses/CourseEditor.react";
import BuilderAnalytics from "./pages/creator/analytics/BuilderAnalytics.react.jsx";
import UnauthArticle from "./pages/player/articles/UnauthArticle.react";
import ProgramEditor from "./pages/creator/programs/ProgramEditor.react";
import Unauthorized from "./components/Unauthorized.react";
import ContestEditor from "./pages/creator/contests/ContestEditor.react";
import ContestsLibrary from "./pages/creator/library/ContestsLibrary.react";
import CreatorHelpDesk from "./pages/helpdesk/CreatorHelpDesk.react";
import LearnerHelpDesk from "./pages/helpdesk/LearnerHelpDesk.react";
import CommunityManager from "./pages/creator/social/CommunityManager.react.jsx";
import RootPage from "./pages/player/home/RootPage.react.jsx";

// Notes for route specifications
// - If the id is course id, please use :courseId
// - If the id is topic id, please use :topicId
// - If the id is deck id, please use :deckId

function App() {
    const isUserLoggedIn = useSelector((state) => state.auth.isUserLoggedIn);
    const userToken = useSelector((state) => state.auth.token);
    const userLang = useSelector((state) => state.auth.user?.lang);
    const userLicense = useSelector((state) => state.auth.user?.license);

    const [size, setSize] = useState([window.innerWidth, window.innerHeight]);
    const isPortrait = window.innerHeight > window.innerWidth;

    const searchParams = new URLSearchParams(document.location.search);
    const authToken = searchParams.get("token");
    const hasToken = authToken !== null;

    let loginPath = settings.auth.routes.login;
    const fromBlacklist = ["/logout"];

    i18next.use(initReactI18next).init({
        interpolation: { escapeValue: false }, // React already does escaping
        lng: userLang, // language to use
        fallbackLng: "en",
        resources: {
            en: {
                common: common_en, // 'common' is our custom namespace
            },
            de: {
                common: common_de,
            },
            hi: {
                common: common_hi,
            },
            mr: {
                common: common_mr,
            },
            bn: {
                common: common_bn,
            },
            kn: {
                common: common_kn,
            },
            ml: {
                common: common_ml,
            },
            ta: {
                common: common_ta,
            },
            te: {
                common: common_te,
            },
        },
    });

    function loadLibraries() {
        store.dispatch(apiV6.endpoints.getArticles.initiate());
        store.dispatch(apiV6.endpoints.getCourseLibrary.initiate());
    }

    useEffect(() => {
        if (isUserLoggedIn) loadLibraries();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUserLoggedIn]);

    function doLogin(location) {
        // Redirect them to the /login page, but save the current location they were
        // trying to go to when they were redirected. This allows us to send them
        // along to that page after they login, which is a nicer user experience
        // than dropping them off on the home page.
        if (hasToken) loginPath += `?token=${authToken}`;
        return (
            <Navigate
                to={
                    loginPath.startsWith("http")
                        ? (window.location.href = loginPath)
                        : loginPath
                }
                state={{
                    from: fromBlacklist.includes(location.pathname)
                        ? null
                        : location,
                }}
                replace
            />
        );
    }
    function RequireAuth() {
        const location = useLocation();

        // Decode the token and get the expiration date
        const decodedToken = jwt.decode(userToken, { complete: true });
        const expirationDate =
            decodedToken && decodedToken.payload.exp
                ? new Date(decodedToken.payload.exp * 1000)
                : null;
        const currentDate = new Date();
        const isTokenValid = currentDate <= expirationDate;

        if (isTokenValid) {
            if (!isUserLoggedIn || hasToken) return doLogin(location);
            else return <Outlet />;
        } else {
            store.dispatch(logout());
            return doLogin(location);
        }
    }
    function DoNotRequireAuth() {
        const location = useLocation();
        if (!isUserLoggedIn) {
            return <Outlet />;
        } else if (location.pathname === loginPath) {
            if (hasToken) return doLogin(location);
            else return <Navigate to={"/"} replace />;
        }
        return false;
    }
    function RequireBuilder() {
        const isBuilder = [
            "SuperAdmin",
            "Admin",
            "Creator",
            "Manager",
            "Trainer",
        ].includes(userLicense.license_type);
        if (isBuilder) {
            return <Outlet />;
        }
        return <Unauthorized />;
    }
    function RequireAdmin() {
        const isAdmin = ["SuperAdmin", "Admin"].includes(
            userLicense.license_type
        );
        if (isAdmin) {
            return <Outlet />;
        }
        return <Unauthorized />;
    }

    // Handle Resizing in the app
    useLayoutEffect(() => {
        const isMobileLandscape =
            window.innerHeight < window.innerWidth && window.innerHeight < 400;
        if (!isMobileLandscape || !settings.mobileBuild) {
            setTimeout(function () {
                let viewheight = window.innerHeight;
                let viewwidth = window.innerWidth;
                let viewport = document.querySelector("meta[name=viewport]");
                viewport.setAttribute(
                    "content",
                    `height=${viewheight}px, width=${viewwidth}px, initial-scale=1`
                );
            }, 300);
        }
    }, []);

    // Handle Resizing in the app
    useLayoutEffect(() => {
        function updateSize() {
            if (!window.doNotResize && devMode)
                setSize([window.innerWidth, window.innerHeight]);
        }
        window.addEventListener("resize", updateSize);
        if (window.innerWidth !== size[0] || window.innerHeight !== size[1])
            updateSize();
        return () => window.removeEventListener("resize", updateSize);
    }, [size]);

    // Handle License based access in the app
    function buildLicenseMap(abilityObject, role) {
        const result = {};

        // Loop through each entity (e.g., "program" and "course") in the ability object
        for (const entity in abilityObject) {
            if (abilityObject.hasOwnProperty(entity)) {
                result[entity] = {};
                // Loop through each action for the current entity
                for (const action in abilityObject[entity]) {
                    if (abilityObject[entity].hasOwnProperty(action)) {
                        // Check if the role has permission for the current action
                        result[entity][action] =
                            abilityObject[entity][action].includes(role);
                    }
                }
            }
        }

        return result;
    }
    const licenseMap =
        isUserLoggedIn && buildLicenseMap(abilityMap, userLicense.license_type);

    return (
        <FlagsProvider features={{ ...featureMap, ...{ ability: licenseMap } }}>
            <I18nextProvider i18n={i18next}>
                <Suspense fallback="loading" size={size}>
                    <BrowserRouter basename={targetRoute}>
                        <Routes>
                            <Route element={<DoNotRequireAuth />}>
                                <Route path="/login" element={<Login />} />
                                <Route
                                    path="/register"
                                    element={<Register />}
                                />
                                <Route
                                    path="/forgot"
                                    element={<ForgotPassword />}
                                />
                                <Route
                                    path="/play/:identifier/:name"
                                    element={<UnauthArticle />}
                                />
                            </Route>
                            <Route element={<RequireAuth />}>
                                <Route
                                    path="/"
                                    element={
                                        settings.functionality.hasHome &&
                                        isPortrait ? (
                                            <AppHome />
                                        ) : (
                                            <Home />
                                        )
                                    }
                                />
                                <Route
                                    path="/goto/:section"
                                    element={<RootPage />}
                                />
                                <Route
                                    path="/learningcenter"
                                    element={<Home />}
                                />
                                <Route
                                    path="/tags/:name"
                                    element={<ArticleListing />}
                                />
                                <Route
                                    path="/content/:readerType/:articleIdentifier"
                                    element={<Article />}
                                />
                                <Route path="/library" element={<Library />} />
                                <Route
                                    path="/programs/:programId"
                                    element={<Program />}
                                />
                                <Route path="/library" element={<Library />} />
                                <Route path="/social" element={<Social />} />
                                <Route
                                    path="/courses/:courseId"
                                    element={<Course />}
                                />
                                <Route
                                    path="/explore/:courseId"
                                    element={<SingleCourse />}
                                />
                                <Route path="/logout" element={<Logout />} />
                                <Route
                                    path="/search"
                                    element={<SearchResults />}
                                />
                                <Route
                                    path="/app/leaderboard"
                                    element={<AppLeaderboard />}
                                />
                                <Route
                                    path="/view/leaderboard"
                                    element={<CleanAppLeaderboard />}
                                />
                                <Route
                                    path="/app/rewards"
                                    element={<AppCollectibles />}
                                />
                                <Route
                                    path="/view/rewards"
                                    element={<CleanAppCollectibles />}
                                />
                                <Route
                                    path="/app/reports"
                                    element={<AppReports />}
                                />
                                <Route
                                    path="/app/helpdesk"
                                    element={<LearnerHelpDesk />}
                                />
                                <Route
                                    path="/editProfile/:isolated"
                                    element={<EditProfile />}
                                />
                                <Route path="/contests" element={<Contest />} />
                                <Route
                                    path="/social/:postId"
                                    element={<Social />}
                                />
                                <Route
                                    path="/setpass"
                                    element={<ChangePassword />}
                                />
                                <Route
                                    path="/comingsoon"
                                    element={<ComingSoon />}
                                />

                                <Route
                                    path="/clients/ekart/dashboard"
                                    element={<GamifixPerformance />}
                                />
                                {/* Creator Routes */}
                                {/* ------------------------------------------ */}
                                <Route element={<RequireBuilder />}>
                                    <Route
                                        path="/builder"
                                        element={<BuilderHome />}
                                    />
                                    <Route
                                        path="/builder/library"
                                        element={<BuilderLibrary />}
                                    />
                                    <Route
                                        path="/builder/contests"
                                        element={<ContestsLibrary />}
                                    />
                                    <Route
                                        path="/builder/programs/:id/:section"
                                        element={<ProgramEditor />}
                                    />
                                    <Route
                                        path="/builder/courses/:id/:section"
                                        element={<CourseEditor />}
                                    />
                                    <Route
                                        path="/builder/contests/:id/:section"
                                        element={<ContestEditor />}
                                    />
                                    <Route
                                        path="/builder/articles/:readerType/:id/:section"
                                        element={<ArticleEditor />}
                                    />
                                    <Route
                                        path="/builder/editor/:readerType/:id/:breadcrumbs"
                                        element={<DeckEditor />}
                                    />
                                    <Route
                                        path="/builder/social"
                                        element={<CommunityManager />}
                                    />
                                    <Route
                                        path="/builder/helpdesk"
                                        element={<CreatorHelpDesk />}
                                    />
                                    <Route
                                        path="/builder/analytics/:id/:section"
                                        element={<BuilderAnalytics />}
                                    />
                                    {/* Admin Routes */}
                                    {/* ------------------------------------------ */}
                                    <Route element={<RequireAdmin />}>
                                        <Route
                                            path="/hotspotter"
                                            element={<HotSpotEditor />}
                                        />
                                    </Route>
                                </Route>
                            </Route>
                        </Routes>
                    </BrowserRouter>
                    <ToastContainer
                        position={isPortrait ? "top-center" : "bottom-left"}
                        autoClose={5000}
                        closeOnClick={true}
                    />
                </Suspense>
            </I18nextProvider>
        </FlagsProvider>
    );
}

export default App;
